import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SITE_NAV, type SiteNavItem } from '@/config/siteNav';
import logo from '@/assets/svg/logo.svg';
import footerBg from '@/assets/home/footer-bg.png';
import wechatIcon from '@/assets/footer/wechat.png';
import wsIcon from '@/assets/footer/ws.png';
import xhsIcon from '@/assets/footer/xhs.png';
import qrWx from '@/assets/footer/qr-wx.png';
import qrWs from '@/assets/footer/qr-ws.png';
import styles from './index.module.scss';

const XHS_LINK = 'https://www.xiaohongshu.com/user/profile/68789009000000001e03ecd4';

const SOCIAL_QR = [
  {
    key: 'wechat',
    icon: wechatIcon,
    iconAlt: '微信',
    qr: qrWx,
    qrAlt: '微信公众号二维码',
    caption: '扫码关注公众号',
  },
  {
    key: 'ws',
    icon: wsIcon,
    iconAlt: '微视',
    qr: qrWs,
    qrAlt: '视频号二维码',
    caption: '扫码关注视频号',
  },
] as const;

const FOOTER_NAV: SiteNavItem[] = [
  ...SITE_NAV.filter((item) => item.key !== 'downloads'),
  { key: 'downloads', label: '下载中心', path: '/aixue-download' },
];

export default function Footer() {
  const { pathname } = useLocation();

  const scrollToTopDeferred = useCallback(() => {
    requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, []);

  return (
    <footer className={styles.footer}>
      <div className={styles.bgMedia} aria-hidden>
        <img src={footerBg} alt="" loading="lazy" />
      </div>
      <div className={styles.inner}>
        <div className={styles.main}>
          <div className={styles.top}>
            <img className={styles.logo} src={logo} alt="爱学" loading="lazy" />
            <nav className={styles.nav} aria-label="页脚导航">
              {FOOTER_NAV.map((item) => {
                if (item.children && item.children.length > 0) {
                  return (
                    <div key={item.key} className={styles.menu}>
                      <span className={styles.navItem} tabIndex={0} aria-haspopup="true">
                        {item.label}
                      </span>
                      <div className={styles.dropdown}>
                        <div className={styles.dropdownPanel}>
                          {item.children.map((child) => (
                            <Link
                              key={child.key}
                              to={child.path}
                              className={`${styles.dropdownItem} ${
                                pathname === child.path ? styles.dropdownItemActive : ''
                              }`}
                              onClick={scrollToTopDeferred}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={styles.navItem}
                    onClick={scrollToTopDeferred}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className={styles.social}>
            <span>关注我们</span>
            <div className={styles.socialBtns}>
              {SOCIAL_QR.map((item) => (
                <div key={item.key} className={styles.socialItem}>
                  <div
                    className={styles.iconBtn}
                    tabIndex={0}
                    aria-describedby={`footer-${item.key}-tip`}
                  >
                    <img src={item.icon} alt={item.iconAlt} loading="lazy" />
                  </div>
                  <div
                    id={`footer-${item.key}-tip`}
                    className={`${styles.popover} ${styles.qrPopover}`}
                    role="tooltip"
                  >
                    <img src={item.qr} alt={item.qrAlt} loading="lazy" />
                    <p className={styles.qrCaption}>{item.caption}</p>
                  </div>
                </div>
              ))}
              <div className={styles.socialItem}>
                <a
                  className={styles.iconBtn}
                  href={XHS_LINK}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="小红书"
                  aria-describedby="footer-xhs-tip"
                >
                  <img src={xhsIcon} alt="小红书" loading="lazy" />
                </a>
                <div
                  id="footer-xhs-tip"
                  className={`${styles.popover} ${styles.tipPopover}`}
                  role="tooltip"
                >
                  <a
                    className={styles.tipBtn}
                    href={XHS_LINK}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="小红书"
                    aria-describedby="footer-xhs-tip"
                  >
                    点击跳转到小红书
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.meta}>
          <p>北京与爱为舞科技有限公司 版权所有</p>
          <p>
            <a
              href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010802046217"
              target="_blank"
              rel="noreferrer"
            >
              京公网安备11010802046217号
            </a>
            <a href="https://beian.miit.gov.cn" target="_blank" rel="noreferrer">
              京ICP备2023011465号-2
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
